/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AggiornaPersonaGiuridicaRequest,
  EliminaOperatoriPersonaGiuridicaRequest,
  GetPersoneGiuridicheIdOperatoriParams,
  GetPersoneGiuridicheOperatoriParams,
  GetPersoneGiuridicheParams,
  OperatorePersonaGiuridicaDTO,
  OperatorePersonaGiuridicaRequest,
  PersonaGiuridicaDTO,
  PersonaGiuridicaRequest,
  WsRispostaPaginataListOperatorePersonaGiuridicaDTO,
  WsRispostaPaginataListPersonaGiuridicaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce persona giuridica a partire da id, inclusi gli operatori associati
 * @summary Restituisce una persona giuridica con i suoi operatori
 */
export const getPersoneGiuridicheId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PersonaGiuridicaDTO>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPersoneGiuridicheIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/persone-giuridiche/${id}`] as const;

export const getGetPersoneGiuridicheIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridicheId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPersoneGiuridicheId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetPersoneGiuridicheIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPersoneGiuridicheId>>
  > = ({ signal }) => getPersoneGiuridicheId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetPersoneGiuridicheIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPersoneGiuridicheId>>
>;
export type GetPersoneGiuridicheIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce una persona giuridica con i suoi operatori
 */
export const useGetPersoneGiuridicheId = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridicheId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPersoneGiuridicheIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna i campi della persona giuridica (non gli operatori)
 * @summary Aggiorna i dati di una persona giuridica
 */
export const putPersoneGiuridicheId = (
  id: string,
  personaGiuridicaRequest: PersonaGiuridicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: personaGiuridicaRequest,
    },
    options,
  );
};

export const getPutPersoneGiuridicheIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putPersoneGiuridicheId>>,
    TError,
    { id: string; data: PersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putPersoneGiuridicheId>>,
  TError,
  { id: string; data: PersonaGiuridicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putPersoneGiuridicheId>>,
    { id: string; data: PersonaGiuridicaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putPersoneGiuridicheId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutPersoneGiuridicheIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putPersoneGiuridicheId>>
>;
export type PutPersoneGiuridicheIdMutationBody = PersonaGiuridicaRequest;
export type PutPersoneGiuridicheIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna i dati di una persona giuridica
 */
export const usePutPersoneGiuridicheId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putPersoneGiuridicheId>>,
    TError,
    { id: string; data: PersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutPersoneGiuridicheIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella persona giuridica e tutti gli operatori associati
 * @summary Cancella una persona giuridica e i suoi operatori
 */
export const deletePersoneGiuridicheId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/persone-giuridiche/${id}`, method: 'delete' },
    options,
  );
};

export const getDeletePersoneGiuridicheIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePersoneGiuridicheId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePersoneGiuridicheId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePersoneGiuridicheId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deletePersoneGiuridicheId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePersoneGiuridicheIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePersoneGiuridicheId>>
>;

export type DeletePersoneGiuridicheIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella una persona giuridica e i suoi operatori
 */
export const useDeletePersoneGiuridicheId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePersoneGiuridicheId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeletePersoneGiuridicheIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce un operatore a partire da id
 * @summary Restituisce un operatore
 */
export const getPersoneGiuridicheIdOperatoriIdOperatore = (
  id: string,
  idOperatore: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<OperatorePersonaGiuridicaDTO>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/operatori/${idOperatore}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPersoneGiuridicheIdOperatoriIdOperatoreQueryKey = (
  id: string,
  idOperatore: string,
) =>
  [
    `/framework-cfg-utenti/persone-giuridiche/${id}/operatori/${idOperatore}`,
  ] as const;

export const getGetPersoneGiuridicheIdOperatoriIdOperatoreQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idOperatore: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPersoneGiuridicheIdOperatoriIdOperatoreQueryKey(id, idOperatore);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>>
  > = ({ signal }) =>
    getPersoneGiuridicheIdOperatoriIdOperatore(
      id,
      idOperatore,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(id && idOperatore), ...queryOptions };
};

export type GetPersoneGiuridicheIdOperatoriIdOperatoreQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>>
>;
export type GetPersoneGiuridicheIdOperatoriIdOperatoreQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce un operatore
 */
export const useGetPersoneGiuridicheIdOperatoriIdOperatore = <
  TData = Awaited<
    ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idOperatore: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatoriIdOperatore>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetPersoneGiuridicheIdOperatoriIdOperatoreQueryOptions(
      id,
      idOperatore,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna i dati di un operatore. Se si rimuove il ruolo RL, verifica che ne resti almeno un altro sulla PG
 * @summary Aggiorna un operatore
 */
export const putPersoneGiuridicheIdOperatoriIdOperatore = (
  id: string,
  idOperatore: string,
  operatorePersonaGiuridicaRequest: OperatorePersonaGiuridicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/operatori/${idOperatore}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: operatorePersonaGiuridicaRequest,
    },
    options,
  );
};

export const getPutPersoneGiuridicheIdOperatoriIdOperatoreMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putPersoneGiuridicheIdOperatoriIdOperatore>>,
    TError,
    { id: string; idOperatore: string; data: OperatorePersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putPersoneGiuridicheIdOperatoriIdOperatore>>,
  TError,
  { id: string; idOperatore: string; data: OperatorePersonaGiuridicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putPersoneGiuridicheIdOperatoriIdOperatore>>,
    { id: string; idOperatore: string; data: OperatorePersonaGiuridicaRequest }
  > = props => {
    const { id, idOperatore, data } = props ?? {};

    return putPersoneGiuridicheIdOperatoriIdOperatore(
      id,
      idOperatore,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutPersoneGiuridicheIdOperatoriIdOperatoreMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof putPersoneGiuridicheIdOperatoriIdOperatore>>
  >;
export type PutPersoneGiuridicheIdOperatoriIdOperatoreMutationBody =
  OperatorePersonaGiuridicaRequest;
export type PutPersoneGiuridicheIdOperatoriIdOperatoreMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiorna un operatore
 */
export const usePutPersoneGiuridicheIdOperatoriIdOperatore = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putPersoneGiuridicheIdOperatoriIdOperatore>>,
    TError,
    { id: string; idOperatore: string; data: OperatorePersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutPersoneGiuridicheIdOperatoriIdOperatoreMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove un operatore. Non è possibile rimuovere l'ultimo RAPPRESENTANTE_LEGALE della PG
 * @summary Rimuove un operatore
 */
export const deletePersoneGiuridicheIdOperatoriIdOperatore = (
  id: string,
  idOperatore: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/operatori/${idOperatore}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeletePersoneGiuridicheIdOperatoriIdOperatoreMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePersoneGiuridicheIdOperatoriIdOperatore>>,
    TError,
    { id: string; idOperatore: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePersoneGiuridicheIdOperatoriIdOperatore>>,
  TError,
  { id: string; idOperatore: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePersoneGiuridicheIdOperatoriIdOperatore>>,
    { id: string; idOperatore: string }
  > = props => {
    const { id, idOperatore } = props ?? {};

    return deletePersoneGiuridicheIdOperatoriIdOperatore(
      id,
      idOperatore,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePersoneGiuridicheIdOperatoriIdOperatoreMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof deletePersoneGiuridicheIdOperatoriIdOperatore>>
  >;

export type DeletePersoneGiuridicheIdOperatoriIdOperatoreMutationError =
  ErrorType<unknown>;

/**
 * @summary Rimuove un operatore
 */
export const useDeletePersoneGiuridicheIdOperatoriIdOperatore = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePersoneGiuridicheIdOperatoriIdOperatore>>,
    TError,
    { id: string; idOperatore: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeletePersoneGiuridicheIdOperatoriIdOperatoreMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca una serie di persone giuridiche. Paginata, filtrabile e ordinabile per i campi principali (ragioneSociale, partitaIva, codiceAteco, codiceRea, formaGiuridica, tipoPg, paeseSedeLegale, provincia, comune, cap, telefono, pec, peo, statoAccreditamento). Restituisce la versione lite (senza operatori).
 * @summary Cerca una serie di persone giuridiche.
 */
export const getPersoneGiuridiche = (
  params?: GetPersoneGiuridicheParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListPersonaGiuridicaDTO>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetPersoneGiuridicheQueryKey = (
  params?: GetPersoneGiuridicheParams,
) =>
  [
    `/framework-cfg-utenti/persone-giuridiche`,
    ...(params ? [params] : []),
  ] as const;

export const getGetPersoneGiuridicheQueryOptions = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridiche>>,
  TError = ErrorType<unknown>,
>(
  params?: GetPersoneGiuridicheParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridiche>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPersoneGiuridiche>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetPersoneGiuridicheQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPersoneGiuridiche>>
  > = ({ signal }) => getPersoneGiuridiche(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetPersoneGiuridicheQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPersoneGiuridiche>>
>;
export type GetPersoneGiuridicheQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di persone giuridiche.
 */
export const useGetPersoneGiuridiche = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridiche>>,
  TError = ErrorType<unknown>,
>(
  params?: GetPersoneGiuridicheParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridiche>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPersoneGiuridicheQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea una persona giuridica. Richiede almeno un operatore con ruolo RAPPRESENTANTE_LEGALE. L'ufficio accreditatore viene ricavato automaticamente dall'ufficio selezionato dell'operatore loggato.
 * @summary Crea una persona giuridica con i suoi operatori
 */
export const postPersoneGiuridiche = (
  personaGiuridicaRequest: PersonaGiuridicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: personaGiuridicaRequest,
    },
    options,
  );
};

export const getPostPersoneGiuridicheMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postPersoneGiuridiche>>,
    TError,
    { data: PersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postPersoneGiuridiche>>,
  TError,
  { data: PersonaGiuridicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postPersoneGiuridiche>>,
    { data: PersonaGiuridicaRequest }
  > = props => {
    const { data } = props ?? {};

    return postPersoneGiuridiche(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPersoneGiuridicheMutationResult = NonNullable<
  Awaited<ReturnType<typeof postPersoneGiuridiche>>
>;
export type PostPersoneGiuridicheMutationBody = PersonaGiuridicaRequest;
export type PostPersoneGiuridicheMutationError = ErrorType<unknown>;

/**
 * @summary Crea una persona giuridica con i suoi operatori
 */
export const usePostPersoneGiuridiche = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postPersoneGiuridiche>>,
    TError,
    { data: PersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostPersoneGiuridicheMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce gli operatori associati a una persona giuridica. Paginata, filtrabile e ordinabile per i campi principali (codiceFiscaleOIdEstero, emailPeo, emailPec, flRappresentanteLegale, scadenzaRapporto, flAttivo). Il filtro per persona giuridica è forzato sull'id passato come path variable.
 * @summary Cerca gli operatori di una persona giuridica.
 */
export const getPersoneGiuridicheIdOperatori = (
  id: string,
  params?: GetPersoneGiuridicheIdOperatoriParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListOperatorePersonaGiuridicaDTO>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/operatori`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetPersoneGiuridicheIdOperatoriQueryKey = (
  id: string,
  params?: GetPersoneGiuridicheIdOperatoriParams,
) =>
  [
    `/framework-cfg-utenti/persone-giuridiche/${id}/operatori`,
    ...(params ? [params] : []),
  ] as const;

export const getGetPersoneGiuridicheIdOperatoriQueryOptions = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetPersoneGiuridicheIdOperatoriParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPersoneGiuridicheIdOperatoriQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>
  > = ({ signal }) =>
    getPersoneGiuridicheIdOperatori(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetPersoneGiuridicheIdOperatoriQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>
>;
export type GetPersoneGiuridicheIdOperatoriQueryError = ErrorType<unknown>;

/**
 * @summary Cerca gli operatori di una persona giuridica.
 */
export const useGetPersoneGiuridicheIdOperatori = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetPersoneGiuridicheIdOperatoriParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheIdOperatori>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPersoneGiuridicheIdOperatoriQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo operatore associato a una persona giuridica. Validazione unicità CF sulla stessa PG
 * @summary Aggiunge un operatore a una persona giuridica
 */
export const postPersoneGiuridicheIdOperatori = (
  id: string,
  operatorePersonaGiuridicaRequest: OperatorePersonaGiuridicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/operatori`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: operatorePersonaGiuridicaRequest,
    },
    options,
  );
};

export const getPostPersoneGiuridicheIdOperatoriMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postPersoneGiuridicheIdOperatori>>,
    TError,
    { id: string; data: OperatorePersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postPersoneGiuridicheIdOperatori>>,
  TError,
  { id: string; data: OperatorePersonaGiuridicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postPersoneGiuridicheIdOperatori>>,
    { id: string; data: OperatorePersonaGiuridicaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postPersoneGiuridicheIdOperatori(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPersoneGiuridicheIdOperatoriMutationResult = NonNullable<
  Awaited<ReturnType<typeof postPersoneGiuridicheIdOperatori>>
>;
export type PostPersoneGiuridicheIdOperatoriMutationBody =
  OperatorePersonaGiuridicaRequest;
export type PostPersoneGiuridicheIdOperatoriMutationError = ErrorType<unknown>;

/**
 * @summary Aggiunge un operatore a una persona giuridica
 */
export const usePostPersoneGiuridicheIdOperatori = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postPersoneGiuridicheIdOperatori>>,
    TError,
    { id: string; data: OperatorePersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostPersoneGiuridicheIdOperatoriMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove gli operatori (rappresentanti legali e non) identificati per codiceFiscaleOIdEstero. Operazione transazionale: se dopo la rimozione la persona giuridica risulterebbe priva di rappresentanti legali viene effettuato rollback.
 * @summary Elimina atomicamente un insieme di operatori di una persona giuridica
 */
export const postPersoneGiuridicheIdEliminaOperatori = (
  id: string,
  eliminaOperatoriPersonaGiuridicaRequest: EliminaOperatoriPersonaGiuridicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/elimina-operatori`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: eliminaOperatoriPersonaGiuridicaRequest,
    },
    options,
  );
};

export const getPostPersoneGiuridicheIdEliminaOperatoriMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postPersoneGiuridicheIdEliminaOperatori>>,
    TError,
    { id: string; data: EliminaOperatoriPersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postPersoneGiuridicheIdEliminaOperatori>>,
  TError,
  { id: string; data: EliminaOperatoriPersonaGiuridicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postPersoneGiuridicheIdEliminaOperatori>>,
    { id: string; data: EliminaOperatoriPersonaGiuridicaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postPersoneGiuridicheIdEliminaOperatori(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPersoneGiuridicheIdEliminaOperatoriMutationResult = NonNullable<
  Awaited<ReturnType<typeof postPersoneGiuridicheIdEliminaOperatori>>
>;
export type PostPersoneGiuridicheIdEliminaOperatoriMutationBody =
  EliminaOperatoriPersonaGiuridicaRequest;
export type PostPersoneGiuridicheIdEliminaOperatoriMutationError =
  ErrorType<unknown>;

/**
 * @summary Elimina atomicamente un insieme di operatori di una persona giuridica
 */
export const usePostPersoneGiuridicheIdEliminaOperatori = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postPersoneGiuridicheIdEliminaOperatori>>,
    TError,
    { id: string; data: EliminaOperatoriPersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostPersoneGiuridicheIdEliminaOperatoriMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Imposta lo stato a VALIDATA o REVOCATA. Non è possibile tornare a BOZZA
 * @summary Cambia lo stato di accreditamento di una persona giuridica
 */
export const patchPersoneGiuridicheIdStatoNuovoStato = (
  id: string,
  nuovoStato: 'BOZZA' | 'VALIDATA' | 'REVOCATA',
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/stato/${nuovoStato}`,
      method: 'patch',
    },
    options,
  );
};

export const getPatchPersoneGiuridicheIdStatoNuovoStatoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdStatoNuovoStato>>,
    TError,
    { id: string; nuovoStato: 'BOZZA' | 'VALIDATA' | 'REVOCATA' },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof patchPersoneGiuridicheIdStatoNuovoStato>>,
  TError,
  { id: string; nuovoStato: 'BOZZA' | 'VALIDATA' | 'REVOCATA' },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdStatoNuovoStato>>,
    { id: string; nuovoStato: 'BOZZA' | 'VALIDATA' | 'REVOCATA' }
  > = props => {
    const { id, nuovoStato } = props ?? {};

    return patchPersoneGiuridicheIdStatoNuovoStato(
      id,
      nuovoStato,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchPersoneGiuridicheIdStatoNuovoStatoMutationResult = NonNullable<
  Awaited<ReturnType<typeof patchPersoneGiuridicheIdStatoNuovoStato>>
>;

export type PatchPersoneGiuridicheIdStatoNuovoStatoMutationError =
  ErrorType<unknown>;

/**
 * @summary Cambia lo stato di accreditamento di una persona giuridica
 */
export const usePatchPersoneGiuridicheIdStatoNuovoStato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdStatoNuovoStato>>,
    TError,
    { id: string; nuovoStato: 'BOZZA' | 'VALIDATA' | 'REVOCATA' },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPatchPersoneGiuridicheIdStatoNuovoStatoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna i dati della persona giuridica (partial update) e sincronizza la lista degli operatori in un unico blocco transazionale. La modalità di sincronizzazione è controllata da tipoAggiornamento (SOSTITUISCI di default: cancella i preesistenti e ricrea la lista; DIFF: upsert per codice fiscale senza cancellare quelli non citati, mantenendo il vincolo di almeno un RAPPRESENTANTE_LEGALE)
 * @summary Aggiorna atomicamente una persona giuridica e i suoi operatori
 */
export const patchPersoneGiuridicheIdAggiornaConOperatori = (
  id: string,
  aggiornaPersonaGiuridicaRequest: AggiornaPersonaGiuridicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/${id}/aggiorna-con-operatori`,
      method: 'patch',
      headers: { 'Content-Type': 'application/json' },
      data: aggiornaPersonaGiuridicaRequest,
    },
    options,
  );
};

export const getPatchPersoneGiuridicheIdAggiornaConOperatoriMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdAggiornaConOperatori>>,
    TError,
    { id: string; data: AggiornaPersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof patchPersoneGiuridicheIdAggiornaConOperatori>>,
  TError,
  { id: string; data: AggiornaPersonaGiuridicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdAggiornaConOperatori>>,
    { id: string; data: AggiornaPersonaGiuridicaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return patchPersoneGiuridicheIdAggiornaConOperatori(
      id,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchPersoneGiuridicheIdAggiornaConOperatoriMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdAggiornaConOperatori>>
  >;
export type PatchPersoneGiuridicheIdAggiornaConOperatoriMutationBody =
  AggiornaPersonaGiuridicaRequest;
export type PatchPersoneGiuridicheIdAggiornaConOperatoriMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiorna atomicamente una persona giuridica e i suoi operatori
 */
export const usePatchPersoneGiuridicheIdAggiornaConOperatori = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchPersoneGiuridicheIdAggiornaConOperatori>>,
    TError,
    { id: string; data: AggiornaPersonaGiuridicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPatchPersoneGiuridicheIdAggiornaConOperatoriMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce gli operatori associati a una persona giuridica. Paginata, filtrabile e ordinabile per i campi principali (codiceFiscaleOIdEstero, emailPeo, emailPec, flRappresentanteLegale, scadenzaRapporto, flAttivo). Il filtro per persona giuridica è forzato sull'id passato come path variable.
 * @summary Cerca gli operatori di una persona giuridica, firma per chiamata da widget (con queryParam).
 */
export const getPersoneGiuridicheOperatori = (
  params: GetPersoneGiuridicheOperatoriParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListOperatorePersonaGiuridicaDTO>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/operatori`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetPersoneGiuridicheOperatoriQueryKey = (
  params: GetPersoneGiuridicheOperatoriParams,
) =>
  [
    `/framework-cfg-utenti/persone-giuridiche/operatori`,
    ...(params ? [params] : []),
  ] as const;

export const getGetPersoneGiuridicheOperatoriQueryOptions = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>,
  TError = ErrorType<unknown>,
>(
  params: GetPersoneGiuridicheOperatoriParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetPersoneGiuridicheOperatoriQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>
  > = ({ signal }) =>
    getPersoneGiuridicheOperatori(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetPersoneGiuridicheOperatoriQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>
>;
export type GetPersoneGiuridicheOperatoriQueryError = ErrorType<unknown>;

/**
 * @summary Cerca gli operatori di una persona giuridica, firma per chiamata da widget (con queryParam).
 */
export const useGetPersoneGiuridicheOperatori = <
  TData = Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>,
  TError = ErrorType<unknown>,
>(
  params: GetPersoneGiuridicheOperatoriParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheOperatori>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPersoneGiuridicheOperatoriQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Lookup esatto per partita IVA. Ritorna null (body vuoto) se non trovata.
 * @summary Restituisce una persona giuridica a partire dalla partita IVA
 */
export const getPersoneGiuridicheByPartitaIvaPartitaIva = (
  partitaIva: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PersonaGiuridicaDTO>(
    {
      url: `/framework-cfg-utenti/persone-giuridiche/byPartitaIva/${partitaIva}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPersoneGiuridicheByPartitaIvaPartitaIvaQueryKey = (
  partitaIva: string,
) =>
  [
    `/framework-cfg-utenti/persone-giuridiche/byPartitaIva/${partitaIva}`,
  ] as const;

export const getGetPersoneGiuridicheByPartitaIvaPartitaIvaQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>
  >,
  TError = ErrorType<unknown>,
>(
  partitaIva: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPersoneGiuridicheByPartitaIvaPartitaIvaQueryKey(partitaIva);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>>
  > = ({ signal }) =>
    getPersoneGiuridicheByPartitaIvaPartitaIva(
      partitaIva,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!partitaIva, ...queryOptions };
};

export type GetPersoneGiuridicheByPartitaIvaPartitaIvaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>>
>;
export type GetPersoneGiuridicheByPartitaIvaPartitaIvaQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce una persona giuridica a partire dalla partita IVA
 */
export const useGetPersoneGiuridicheByPartitaIvaPartitaIva = <
  TData = Awaited<
    ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>
  >,
  TError = ErrorType<unknown>,
>(
  partitaIva: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPersoneGiuridicheByPartitaIvaPartitaIva>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetPersoneGiuridicheByPartitaIvaPartitaIvaQueryOptions(
      partitaIva,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
